import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { ImagePlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AvatarUploadControl } from './AvatarUploadControl';
import { useAuth } from '@/contexts/AuthContext';
import { db } from '@/lib/firebase';

// Session guard: the Firestore "prompt seen" write can lag or fail, which
// re-fired the dialog on subsequent navigations. Never show more than once
// per browser session regardless of profile state.
const SESSION_PROMPT_KEY = 'rc_avatar_prompt_shown';

const hasPromptedThisSession = (): boolean => {
  try {
    return sessionStorage.getItem(SESSION_PROMPT_KEY) === '1';
  } catch {
    return false;
  }
};

const markPromptedThisSession = (): void => {
  try {
    sessionStorage.setItem(SESSION_PROMPT_KEY, '1');
  } catch {
    // Ignore — worst case the Firestore guard still applies.
  }
};

export const AvatarPromptDialog: React.FC = () => {
  const { user, userProfile, refreshProfile } = useAuth();
  const [open, setOpen] = useState(false);

  const avatarUrl = userProfile?.avatarUrl || userProfile?.photoURL || user?.photoURL || null;
  const loginCount = userProfile?.loginCount || 0;
  const lastPromptCount = userProfile?.lastAvatarPromptLoginCount || 0;

  const shouldPrompt = useMemo(() => {
    return Boolean(
      user &&
      userProfile &&
      !avatarUrl &&
      loginCount > 0 &&
      loginCount % 10 === 0 &&
      lastPromptCount !== loginCount
    );
  }, [avatarUrl, lastPromptCount, loginCount, user, userProfile]);

  useEffect(() => {
    if (shouldPrompt && !hasPromptedThisSession()) {
      markPromptedThisSession();
      setOpen(true);
    }
  }, [shouldPrompt]);

  const markPromptSeen = async () => {
    if (!user || !loginCount) return;

    try {
      await setDoc(doc(db, 'users', user.uid), {
        lastAvatarPromptLoginCount: loginCount,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      await refreshProfile();
    } catch (error) {
      console.error('Failed to mark avatar prompt as seen:', error);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen && shouldPrompt) {
      void markPromptSeen();
    }
  };

  const handleUploaded = async () => {
    await markPromptSeen();
    setOpen(false);
  };

  if (!user || !userProfile) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md overflow-hidden border-border p-0">
        <div className="bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 p-6 text-white">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/15">
            <ImagePlus className="h-6 w-6" />
          </div>
          <DialogHeader className="text-left">
            <DialogTitle className="text-2xl tracking-[-0.04em]">
              Put a face to the deal flow
            </DialogTitle>
            <DialogDescription className="text-slate-300">
              Your avatar now travels with your deals, portfolio companies, and workspace navigation.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="space-y-5 p-6">
          <AvatarUploadControl
            currentAvatarUrl={avatarUrl}
            displayName={userProfile.displayName || user.displayName}
            email={userProfile.email || user.email}
            buttonLabel="Upload Avatar"
            buttonVariant="default"
            helperText="Professional headshot, firm mascot, or tasteful deal-team alter ego. JPG, PNG, GIF or WebP."
            onUploaded={handleUploaded}
          />

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => handleOpenChange(false)}>
              Later
            </Button>
            <Button variant="outline" asChild>
              <Link to="/settings?tab=profile" onClick={() => handleOpenChange(false)}>
                Open Profile
              </Link>
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
